import { useCallback, useState } from 'react';
import type React from 'react';
import type { ProjectOverviewRow } from '../types';
import { readExpandAll, writeExpandAll } from '../../../table/tablePreferencesStorage';
import { BY_PROJECT_TABLE_SORT } from './useOverviewTabState';

// Active and archived project views share the by-project table, and so this preference.
const EXPAND_ALL_TABLE_ID = BY_PROJECT_TABLE_SORT.tableId;

export function getProjectRowKey(project: ProjectOverviewRow): string {
    return project.projectRelayId;
}

/** Id of a project's expanded rows, referenced by its expand button's `aria-controls`. */
export function getProjectDetailsId(project: ProjectOverviewRow): string {
    return `${getProjectRowKey(project)}-details`;
}

export function useExpandedProjectRows({ projectRows }: { projectRows: ProjectOverviewRow[] }) {
    const [expandAll, setExpandAllState] = useState(() => readExpandAll(EXPAND_ALL_TABLE_ID));
    const [expandedProjects, setExpandedProjects] = useState<Set<string>>(() => new Set());

    const setExpandAll = useCallback((nextExpandAll: boolean) => {
        setExpandAllState(nextExpandAll);
        writeExpandAll(EXPAND_ALL_TABLE_ID, nextExpandAll);
        // Turning it off collapses every row; turning it on makes the set irrelevant.
        setExpandedProjects(new Set());
    }, []);

    const isProjectExpanded = useCallback(
        (project: ProjectOverviewRow) =>
            expandAll || expandedProjects.has(getProjectRowKey(project)),
        [expandAll, expandedProjects]
    );

    const toggleProjectRow = useCallback(
        (project: ProjectOverviewRow, event?: React.MouseEvent<HTMLTableRowElement>) => {
            const target = event?.target as Element | null;
            // Prevents row expansion by ignoring clicks inside elements
            // with `data-stop-row-toggle="true"` (e.g., OptionsMenu).
            if (target?.closest('[data-stop-row-toggle="true"]')) {
                return;
            }

            const projectRowKey = getProjectRowKey(project);

            if (expandAll) {
                // Collapsing one row ends "expand all", but keeps the other rows open.
                setExpandAllState(false);
                writeExpandAll(EXPAND_ALL_TABLE_ID, false);
                setExpandedProjects(
                    new Set(
                        projectRows
                            .map(getProjectRowKey)
                            .filter((rowKey) => rowKey !== projectRowKey)
                    )
                );
                return;
            }

            setExpandedProjects((current) => {
                const next = new Set(current);
                if (next.has(projectRowKey)) {
                    next.delete(projectRowKey);
                } else {
                    next.add(projectRowKey);
                }
                return next;
            });
        },
        [expandAll, projectRows]
    );

    return {
        expandAll,
        setExpandAll,
        setExpandedProjects,
        isProjectExpanded,
        toggleProjectRow,
    };
}
